import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState } from "react";
import { Button, DateTimeInput, Form, FormField, FormInput } from "@/components/ui";
import { Card, CardContent } from "@/components/ui/card";
import { useCrew } from "@/context/CrewProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ActivityIndicator, View, StyleSheet, Text, TouchableOpacity, Modal, Alert, Platform } from "react-native";
import { z } from "zod";
import { yourDetailsFormSchema, mapCrewForToFrontend } from "./types";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { addWeeks, format } from 'date-fns'
import { Ionicons } from '@expo/vector-icons';
import colors from '@/constants/Colors';
import { useSupabase } from "@/hooks/useSupabase";
import { SelectInput } from '@/components/ui/select-input';


// Define the shape of a pending change
interface PendingChange {
  id: number;
  crew_id: number;
  key: string;
  value: string;
  status: number;
}

// Define the type for initialValues to fix TypeScript error
type FormValues = z.infer<typeof yourDetailsFormSchema>;

interface YourDetailsFormProps {
  onFormSuccess: () => void;
  onFormChange: (hasChanges: boolean) => void;
  pendingChanges: PendingChange[];
}

export type YourDetailsFormRef = {
  submit: () => void;
};

const YourDetailsForm = forwardRef<YourDetailsFormRef, YourDetailsFormProps>(
  ({ onFormSuccess, onFormChange, pendingChanges }, ref) => {
    const { currentCrewMember } = useCrew();
    const { supabase, signOut } = useSupabase();
    const initialValues = currentCrewMember ? mapCrewForToFrontend(currentCrewMember) : {} as FormValues;
    const initialValuesRef = useRef<FormValues>(initialValues);

    // Get contract type history to determine latest contract type
    const { data: contractTypeHistory } = trpc.crew.listContractTypeHistory.useQuery(
      Number(currentCrewMember?.id),
      {
        enabled: !!currentCrewMember?.id
      }
    );

    // Determine if the payroll preference should be shown based on the latest contract type
    // The contract history is already sorted by date desc, so the first item is the latest
    const latestContractType = contractTypeHistory && contractTypeHistory.length > 0
      ? contractTypeHistory[0].type_id
      : currentCrewMember?.contract_type;

    const showPayrollPreference = latestContractType === 0 || latestContractType === 2 || latestContractType === 4;

    // Modal state for showing pending change
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [selectedChange, setSelectedChange] = useState<PendingChange | null>(null);

    const form = useForm<FormValues>({
      resolver: zodResolver(yourDetailsFormSchema),
      defaultValues: initialValues
    });

    // Function to check if a field has pending changes
    const hasPendingChange = (fieldName: string): PendingChange | undefined => {
      return pendingChanges.find(change => change.key === fieldName);
    };

    // Function to display the pending value
    const displayPendingValue = (change: PendingChange) => {
      // Handle boolean fields
      if (change.key === 'social_media_allowed') {
        return change.value === 'true' ? 'Allowed' : 'Not Allowed'
      }
      // Handle date fields differently
      if (change.key === 'date_of_birth' || change.key === 'date_moved_in') {
        try {
          const date = new Date(change.value);
          return format(date, 'dd/MM/yyyy');
        } catch (e) {
          return change.value;
        }
      }
      return change.value;
    };

    // Show popover with pending change details
    const showPendingChange = (change: PendingChange) => {
      setSelectedChange(change);
      setPopoverVisible(true);
    };

    // Track form changes and notify parent component
    useEffect(() => {
      const subscription = form.watch((formValues) => {
        // Compare current form values with initial values
        let hasChanges = false;

        Object.keys(formValues).forEach(key => {
          const fieldKey = key as keyof FormValues;
          const currentValue = formValues[fieldKey];
          const initialValue = initialValuesRef.current[fieldKey];

          // Special handling for date fields
          if (currentValue instanceof Date && initialValue instanceof Date) {
            if (currentValue.getTime() !== initialValue.getTime()) {
              hasChanges = true;
            }
          }
          // Handle other field types
          else if (currentValue !== initialValue &&
            currentValue !== undefined &&
            currentValue !== null &&
            currentValue !== '') {
            hasChanges = true;
          }
        });

        onFormChange(hasChanges);
      });

      return () => subscription.unsubscribe();
    }, [form, onFormChange]);

    const { mutate: updateCrewRequest } = trpc.crew.updateCrewRequest.useMutation({
      onSuccess(_response) {
        onFormSuccess();
        Toast.show({
          type: 'success',
          text1: 'Your profile request has been submitted!'
        });
      },
    });

    const { mutate: createTask } = trpc.task.createTask.useMutation({
      onSuccess(_response) { },
    });

    const { mutate: deleteUser } = trpc.auth.deleteUser.useMutation({
      onSuccess(_response) {
        if (signOut) {
          signOut();
        }
        Toast.show({
          type: 'success',
          text1: 'Your account has been deleted successfully.'
        });
      },
    });

    const handleDeleteAccount = () => {
      deleteUser();
    }

    // Function to show delete confirmation
    const confirmDelete = () => {
      Alert.alert(
        'Delete Account',
        'Are you sure you want to delete your account? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', onPress: handleDeleteAccount, style: 'destructive' }
        ],
        { cancelable: true }
      );
    };

    async function onSubmit(data: z.infer<typeof yourDetailsFormSchema>) {
      try {
        if (!currentCrewMember) return;
        const dirtyFields = form.formState.dirtyFields;

        const updatedCrew = Object.keys(dirtyFields)
          .map((fieldName) => {
            const value = data[fieldName as keyof typeof data];
            if (value === undefined || value === null || value === '') {
              return null;
            }
            return {
              key: fieldName,
              value: value.toString()
            };
          })
          .filter((item): item is { key: string; value: string } => item !== null);

        updateCrewRequest({ id: currentCrewMember?.id, updateCrew: updatedCrew });

        createTask({
          title: `New Crew Profile Update for ${currentCrewMember?.name} (${updatedCrew.length} ${updatedCrew.length === 1 ? 'change' : 'changes'})`,
          description: `New Crew Profile Update for ${currentCrewMember?.name} (${updatedCrew.length} ${updatedCrew.length === 1 ? 'change' : 'changes'})`,
          priority: 1,
          tags: ["crew profile"],
          due_date: addWeeks(new Date(), 1),
          crew_id: currentCrewMember?.id,
          state_name: "Open",
          type: "crew.update",
          team_id: 2
        })

        form.reset();
      } catch (error: Error | any) {
        Toast.show({
          type: 'error',
          text1: 'Failed to save settings, please try again.'
        });
      }
    }

    // Expose the submit method to the parent component
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(onSubmit)();
      }
    }));

    // Custom input wrapper that includes a pending change indicator
    const FieldWithPendingIndicator = ({ name, children }: { name: string, children: React.ReactNode }) => {
      const pendingChange = hasPendingChange(name);

      return (
        <View style={styles.fieldWithIndicator}>
          <View style={styles.fieldContainer}>
            {children}
          </View>
          {pendingChange && (
            <TouchableOpacity
              style={styles.pendingIndicator}
              onPress={() => showPendingChange(pendingChange)}
            >
              <Ionicons name="alert-circle" size={20} color="#FF6B6B" />
            </TouchableOpacity>
          )}
        </View>
      );
    };

    return (
      <>
        <Card style={styles.card}>
          <View style={styles.headerContainer}>
            <View style={styles.iconWrapper}>
              <Ionicons name="person" size={18} color="white" />
            </View>
            <Text style={styles.headerTitle}>Your Details</Text>
          </View>
          <CardContent style={styles.cardContent}>
            <Form {...form}>
              <View style={styles.formContainer}>
                {/* Personal Information Section */}
                <View style={styles.inputGroup}>
                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="person" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Name</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="name">
                          <FormInput
                            label=""
                            placeholder="Must appear as it does on your passport"
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="id-card" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Nick Name</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="nick_name"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="nick_name">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="calendar" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Date of Birth</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="date_of_birth"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="date_of_birth">
                          <DateTimeInput
                            {...field}
                            label=""
                            value={field.value || null}
                            style={styles.dateInput}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="call" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Mobile</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="mobile"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="mobile">
                          <FormInput
                            label=""
                            keyboardType="phone-pad"
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="mail" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Email</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="email">
                          <FormInput
                            label=""
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect={false}
                            keyboardType="email-address"
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>
                </View>

                {/* Address Section */}
                <View style={styles.sectionHeader}>
                  <Ionicons name="home" size={16} color="#222" />
                  <Text style={styles.sectionTitle}>Address</Text>
                </View>

                <View style={styles.inputGroup}>
                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="location" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Address Line 1</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="address_line_1"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="address_line_1">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="location" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Address Line 2</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="address_line_2"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="address_line_2">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="location" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Address Line 3</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="address_line_3"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="address_line_3">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="navigate" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Postcode</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="address_postcode"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="address_postcode">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="calendar" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Date Moved In</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="date_moved_in"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="date_moved_in">
                          <DateTimeInput
                            {...field}
                            label=""
                            value={field.value || null}
                            style={styles.dateInput}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>
                </View>

                {/* Personal Preferences Section */}
                <View style={styles.sectionHeader}>
                  <Ionicons name="person-circle" size={16} color="#222" />
                  <Text style={styles.sectionTitle}>Personal Preferences</Text>
                </View>

                <View style={styles.inputGroup}>
                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="male-female" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Gender</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="gender">
                          <SelectInput
                            options={[
                              { label: "Male", value: "male" },
                              { label: "Female", value: "female" },
                              { label: "Other", value: "other" },
                              { label: "Prefer not to say", value: "prefer_not_to_say" },
                            ]}
                            value={field.value || null}
                            onChange={(value) => field.onChange(value)}
                            placeholder="Select gender"
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="person" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Gender Pronoun</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="gender_pronoun"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="gender_pronoun">
                          <SelectInput
                            options={[
                              { label: "He/Him", value: "HE_HIM" },
                              { label: "She/Her", value: "SHE_HER" },
                              { label: "They/Them", value: "THEY_THEM" },
                              { label: "Other", value: "OTHER" },
                            ]}
                            value={field.value || null}
                            onChange={(value) => {
                              field.onChange(value);
                              // If OTHER is not selected, clear the gender_pronoun_other field
                              if (value !== "OTHER") {
                                form.setValue("gender_pronoun_other", "");
                              }
                            }}
                            placeholder="Select pronoun"
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  {/* Only show the custom pronoun field if OTHER is selected */}
                  {form.watch("gender_pronoun") === "OTHER" && (
                    <View style={styles.formSection}>
                      <View style={styles.labelContainer}>
                        <Ionicons name="create" size={16} color="#666" />
                        <Text style={styles.inputLabel}>Custom Pronoun</Text>
                      </View>
                      <FormField
                        control={form.control}
                        name="gender_pronoun_other"
                        render={({ field }) => (
                          <FieldWithPendingIndicator name="gender_pronoun_other">
                            <FormInput
                              label=""
                              style={styles.input}
                              placeholder="Please specify your pronouns"
                              {...field}
                            />
                          </FieldWithPendingIndicator>
                        )}
                      />
                    </View>
                  )}

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="restaurant" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Dietary Requirements</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="dietary_requirements"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="dietary_requirements">
                          <FormInput
                            label=""
                            style={styles.input}
                            placeholder="Any dietary requirements"
                            multiline
                            numberOfLines={3}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  {/* Moved Payroll Preference and Target Pay to the end and grouped them together */}
                  {/* Show these fields only for Sub Contractor (0), Self Employed (2), or Limited Company (4) */}
                  {showPayrollPreference && (

                    <View style={styles.formSection}>
                      <View style={styles.labelContainer}>
                        <Ionicons name="cash" size={16} color="#666" />
                        <Text style={styles.inputLabel}>Payroll Preference</Text>
                      </View>
                      <FormField
                        control={form.control}
                        name="payroll_preference"
                        render={({ field }) => (
                          <FieldWithPendingIndicator name="payroll_preference">
                            <SelectInput
                              options={[
                                { label: "Bi-weekly", value: "BIWEEKLY" },
                                { label: "Monthly", value: "MONTHLY" },
                              ]}
                              value={field.value || null}
                              onChange={(value) => field.onChange(value)}
                              placeholder="Select payroll preference"
                            />
                          </FieldWithPendingIndicator>
                        )}
                      />
                    </View>
                  )}
                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="cash" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Target Pay (£/Month)</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="target_pay"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="target_pay">
                          <FormInput
                            label=""
                            style={styles.input}
                            keyboardType="numeric"
                            placeholder="Your target monthly pay rate (£)"
                            {...field}
                            value={field.value?.toString() || ''}
                            onChangeText={(value) => {
                              // Convert to number or leave empty
                              const numValue = value === '' ? '' : parseFloat(value);
                              field.onChange(numValue);
                            }}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="share-social" size={16} color="#666" />
                      <Text style={styles.inputLabel}>Social Media Consent</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="social_media_allowed"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="social_media_allowed">
                          <SelectInput
                            options={[
                              { label: "Allowed", value: "true" },
                              { label: "Not Allowed", value: "false" },
                            ]}
                            value={field.value === false ? "false" : "true"}
                            onChange={(value) => field.onChange(value === "true")}
                            placeholder="Select option"
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>
                </View>

                <View style={styles.deleteButtonContainer}>
                  <Button
                    size="default"
                    variant="destructive"
                    onPress={confirmDelete}
                  >
                    Delete Account
                  </Button>
                </View>
              </View>
            </Form>
          </CardContent>
        </Card>

        {/* Pending Change Popover */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={popoverVisible}
          onRequestClose={() => setPopoverVisible(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setPopoverVisible(false)}
          >
            <View style={styles.popoverContainer}>
              <View style={styles.popoverHeader}>
                <Ionicons name="alert-circle" size={20} color="#FF6B6B" />
                <Text style={styles.popoverTitle}>Pending Change</Text>
                <TouchableOpacity onPress={() => setPopoverVisible(false)}>
                  <Ionicons name="close" size={22} color="#666" />
                </TouchableOpacity>
              </View>
              {selectedChange && (
                <View style={styles.popoverContent}>
                  <Text style={styles.popoverLabel}>Field:</Text>
                  <Text style={styles.popoverValue}>{selectedChange.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>

                  <Text style={styles.popoverLabel}>New Value:</Text>
                  <Text style={styles.popoverValue}>{displayPendingValue(selectedChange)}</Text>

                  <Text style={styles.popoverNote}>This change is pending approval.</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        </Modal>
      </>
    );
  }
);

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    backgroundColor: '#fff',
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#eee',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginLeft: 8,
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#222',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    padding: 0,
  },
  formContainer: {
    padding: 0,
  },
  inputGroup: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
    marginLeft: 6,
  },
  formSection: {
    marginBottom: 12,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  inputLabel: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '500',
    color: '#444',
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    height: 44,
  },
  dateInput: {
    backgroundColor: '#fff',
    height: 44,
    marginVertical: 0,
    paddingVertical: 0,
  },
  fieldWithIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fieldContainer: {
    flex: 1,
  },
  pendingIndicator: {
    marginLeft: 8,
    padding: 4,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popoverContainer: {
    width: '85%',
    backgroundColor: 'white',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  popoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  popoverTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
    marginLeft: 8,
  },
  popoverContent: {
    padding: 16,
  },
  popoverLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginBottom: 4,
  },
  popoverValue: {
    fontSize: 16,
    color: '#333',
    marginBottom: 16,
  },
  popoverNote: {
    fontSize: 14,
    color: '#FF6B6B',
    fontStyle: 'italic',
    marginTop: 8,
  },
  deleteButtonContainer: {
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    marginTop: 16,
  },
});

export default YourDetailsForm;